import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { firebaseApp } from "../../firebase";
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  ArrowLeft,
  ArrowUpDown,
  Filter,
  CheckCircle,
  XCircle,
  Star,
  Mail,
  Calendar,
  Trophy,
  AlertCircle,
} from "lucide-react";

interface AssessmentDetails {
  finalScore: number;
  detailedScoring: {
    technicalKnowledge: number;
    problemSolving: number;
    communication: number;
    jobFit: number;
  };
  structuredAssessment: {
    technical: string;
    communication: string;
    problemSolving: string;
    jobFit: string;
  };
  narrativeAssessment: string;
  strengths: string[];
  weaknesses: string[];
  recommendationForHiring: string;
  keyFindings: string;
}

interface CandidateResult {
  id: string;
  jobSeekerName: string;
  jobSeekerEmail: string;
  mcqScore: number;
  interviewScore: number;
  totalScore: number;
  completedAt: any;
  recommendationForHiring: string;
  hasCompletedInterview: boolean;
  jobSeekerId: string;
  jobFitAssessment?: string;
  keyFindings?: string;
}

const TestResults: React.FC = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const db = getFirestore(firebaseApp);

  const [candidates, setCandidates] = useState<CandidateResult[]>([]);
  const [filteredCandidates, setFilteredCandidates] = useState<CandidateResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState<"high" | "low">("high");
  const [showOnlyRecommended, setShowOnlyRecommended] = useState(false);
  const [testTitle, setTestTitle] = useState("");
  const [testCreatorId, setTestCreatorId] = useState("");
  const [selectedCandidate, setSelectedCandidate] = useState<CandidateResult | null>(null);
  const [selectedAssessment, setSelectedAssessment] = useState<AssessmentDetails | null>(null);
  const [showDetailedModal, setShowDetailedModal] = useState(false);

  // Fetch test details
  useEffect(() => {
    if (!testId) return;

    const fetchTestDetails = async () => {
      const testDoc = await getDoc(doc(db, "hrTests", testId));
      if (testDoc.exists()) {
        const testData = testDoc.data();
        setTestTitle(testData.testData?.title || testData.jobTitle || "Test Results");
        setTestCreatorId(testData.hrId || "");
      }
    };

    fetchTestDetails();
  }, [testId, db]);

  // Fetch candidates who took this test
  useEffect(() => {
    if (!testId || !user) return;

    const q = query(
      collection(db, "jobseekerResults"),
      where("testId", "==", testId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const results = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as CandidateResult[];

      setCandidates(results);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [testId, user, db]);

  // Apply filtering and sorting
  useEffect(() => {
    let filtered = [...candidates];

    // Filter by AI recommendation
    if (showOnlyRecommended) {
      filtered = filtered.filter(
        (c) =>
          c.recommendationForHiring === "STRONG RECOMMEND" ||
          c.recommendationForHiring === "RECOMMEND"
      );
    }

    // Sort by score
    filtered.sort((a, b) => {
      const scoreA = a.totalScore || a.mcqScore || 0;
      const scoreB = b.totalScore || b.mcqScore || 0;
      return sortOrder === "high" ? scoreB - scoreA : scoreA - scoreB;
    });

    setFilteredCandidates(filtered);
  }, [candidates, sortOrder, showOnlyRecommended]);

  const handleViewDetails = (candidate: CandidateResult) => {
    setSelectedCandidate(candidate);

    // Parse assessment details if available
    if (candidate.jobFitAssessment) {
      try {
        const assessment = JSON.parse(candidate.jobFitAssessment);
        setSelectedAssessment(assessment);
      } catch (error) {
        setSelectedAssessment(null);
      }
    } else {
      setSelectedAssessment(null);
    }

    setShowDetailedModal(true);
  };

  // Check if candidate has completed the test and is eligible for shortlisting/rejection
  const canShortlistOrReject = (candidate: CandidateResult): { canAction: boolean; reason: string } => {
    // Check if test was terminated
    if ((candidate as any).status === 'terminated') {
      return { canAction: false, reason: 'This candidate\'s test was terminated and cannot be shortlisted or rejected.' };
    }

    // Check if test was completed (has interview if required, or has final score)
    if (!candidate.hasCompletedInterview && !candidate.totalScore) {
      return { canAction: false, reason: 'This candidate has not completed the test yet.' };
    }

    // Check if total score is 0 (might indicate incomplete test)
    if (candidate.totalScore === 0 && !candidate.mcqScore) {
      return { canAction: false, reason: 'This candidate has no test results to evaluate.' };
    }

    return { canAction: true, reason: '' };
  };

  const handleShortlist = async (candidate: CandidateResult) => {
    if (!user) return;

    // Check if candidate is eligible
    const eligibility = canShortlistOrReject(candidate);
    if (!eligibility.canAction) {
      alert(eligibility.reason);
      return;
    }

    // Check if already rejected - remove from rejected first
    try {
      const rejectedDocRef = doc(db, "hrRejected", `${user.id}_${candidate.jobSeekerId}`);
      const rejectedDoc = await getDoc(rejectedDocRef);
      if (rejectedDoc.exists()) {
        await deleteDoc(rejectedDocRef);
      }
    } catch (err) {
      // Ignore error if doc doesn't exist
    }

    try {
      await setDoc(
        doc(db, "hrShortlisted", `${user.id}_${candidate.jobSeekerId}`),
        {
          hrId: user.id,
          candidateId: candidate.jobSeekerId || "",
          candidateName: candidate.jobSeekerName || "Unknown",
          candidateEmail: candidate.jobSeekerEmail || "",
          testId: testId || "",
          testTitle: testTitle || "Unknown Test",
          totalScore: candidate.totalScore || 0,
          recommendationForHiring: candidate.recommendationForHiring || "NEUTRAL",
          shortlistedAt: serverTimestamp(),
        }
      );

      alert(`${candidate.jobSeekerName} has been shortlisted!`);
      setShowDetailedModal(false);
      setSelectedCandidate(null);
      setSelectedAssessment(null);
    } catch (error) {
      alert("Failed to shortlist candidate");
    }
  };

  const handleReject = async (candidate: CandidateResult) => {
    if (!user) return;

    // Check if candidate is eligible
    const eligibility = canShortlistOrReject(candidate);
    if (!eligibility.canAction) {
      alert(eligibility.reason);
      return;
    }

    // Check if already shortlisted - remove from shortlisted first
    try {
      const shortlistedDocRef = doc(db, "hrShortlisted", `${user.id}_${candidate.jobSeekerId}`);
      const shortlistedDoc = await getDoc(shortlistedDocRef);
      if (shortlistedDoc.exists()) {
        await deleteDoc(shortlistedDocRef);
      }
    } catch (err) {
      // Ignore error if doc doesn't exist
    }

    try {
      await setDoc(
        doc(db, "hrRejected", `${user.id}_${candidate.jobSeekerId}`),
        {
          hrId: user.id,
          candidateId: candidate.jobSeekerId || "",
          candidateName: candidate.jobSeekerName || "Unknown",
          candidateEmail: candidate.jobSeekerEmail || "",
          testId: testId || "",
          testTitle: testTitle || "Unknown Test",
          totalScore: candidate.totalScore || 0,
          recommendationForHiring: candidate.recommendationForHiring || "NEUTRAL",
          rejectedAt: serverTimestamp(),
        }
      );

      alert(`${candidate.jobSeekerName} has been rejected.`);
      setShowDetailedModal(false);
      setSelectedCandidate(null);
      setSelectedAssessment(null);
    } catch (error) {
      alert("Failed to reject candidate");
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const getRecommendationBadge = (recommendation: string) => {
    switch (recommendation) {
      case "STRONG RECOMMEND":
        return (
          <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
            ⭐ Strong Recommend
          </span>
        );
      case "RECOMMEND":
        return (
          <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
            ✓ Recommend
          </span>
        );
      case "DO NOT RECOMMEND":
        return (
          <span className="px-3 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">
            ✗ Do Not Recommend
          </span>
        );
      default:
        return (
          <span className="px-3 py-1 bg-gray-100 text-gray-800 text-xs font-semibold rounded-full">
            - Neutral
          </span>
        );
    }
  };

  if (loading) {
    return <div className="text-center py-10">Loading test results...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate("/hr/my-tests")}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            Back
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{testTitle}</h1>
            <p className="text-gray-600 mt-1">
              {filteredCandidates.length} candidate{filteredCandidates.length !== 1 ? "s" : ""} found
            </p>
          </div>
        </div>
      </div>

      {/* Filters and Sorting */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Sort Dropdown */}
          <div className="flex items-center space-x-2">
            <ArrowUpDown className="w-4 h-4 text-gray-600" />
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as "high" | "low")}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="high">Highest to Lowest</option>
              <option value="low">Lowest to Highest</option>
            </select>
          </div>

          {/* AI Recommendation Filter */}
          <button
            onClick={() => setShowOnlyRecommended(!showOnlyRecommended)}
            className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              showOnlyRecommended
                ? "bg-green-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            <Filter className="w-4 h-4 mr-2" />
            {showOnlyRecommended ? "Showing Recommended Only" : "Strong Recommendation by AI"}
          </button>

          <div className="ml-auto text-sm text-gray-600">
            Total Candidates: {candidates.length}
          </div>
        </div>
      </div>

      {/* No Results */}
      {filteredCandidates.length === 0 && (
        <div className="text-center py-20 bg-white rounded-xl border border-gray-200">
          <p className="text-gray-600">
            {showOnlyRecommended
              ? "No recommended candidates found for this test."
              : "No candidates have taken this test yet."}
          </p>
        </div>
      )}

      {/* Results Table */}
      {filteredCandidates.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Candidate
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Round 1 (MCQ)
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Round 2 (Interview)
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Total Score
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    AI Recommendation
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Completed On
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredCandidates.map((candidate) => (
                  <tr
                    key={candidate.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => handleViewDetails(candidate)}
                  >
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{candidate.jobSeekerName}</p>
                        <p className="text-sm text-gray-600 flex items-center mt-1">
                          <Mail className="w-3 h-3 mr-1" />
                          {candidate.jobSeekerEmail}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-semibold text-blue-600">
                        {candidate.mcqScore?.toFixed(1) || 0}/25
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {candidate.hasCompletedInterview ? (
                        <span className="font-semibold text-green-600">
                          {candidate.interviewScore?.toFixed(1) || 0}/75
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">Not Completed</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <Trophy className="w-4 h-4 text-yellow-500" />
                        <span className="font-bold text-gray-900">
                          {candidate.totalScore?.toFixed(1) || candidate.mcqScore?.toFixed(1) || 0}/100
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getRecommendationBadge(candidate.recommendationForHiring)}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600 flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        {formatDate(candidate.completedAt)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails(candidate);
                        }}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        View Details →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detailed Assessment Modal */}
      {showDetailedModal && selectedCandidate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full my-8 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {selectedCandidate.jobSeekerName}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1 flex items-center">
                    <Mail className="w-4 h-4 mr-1" />
                    {selectedCandidate.jobSeekerEmail}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowDetailedModal(false);
                    setSelectedCandidate(null);
                    setSelectedAssessment(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Score Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-800 font-medium mb-1">Round 1 (MCQ)</p>
                  <p className="text-2xl font-bold text-blue-900">{selectedCandidate.mcqScore?.toFixed(1) || 0}/25</p>
                  <p className="text-xs text-blue-600 mt-1">25% weight</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-green-800 font-medium mb-1">Round 2 (AI Interview)</p>
                  <p className="text-2xl font-bold text-green-900">{selectedCandidate.interviewScore?.toFixed(1) || 0}/75</p>
                  <p className="text-xs text-green-600 mt-1">75% weight</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm text-purple-800 font-medium mb-1">Total Score</p>
                  <p className="text-2xl font-bold text-purple-900">{selectedCandidate.totalScore?.toFixed(1) || 0}/100</p>
                  <p className="text-xs text-purple-600 mt-1">Combined</p>
                </div>
              </div>

              {/* Access Control Check - Only show full details to test creator */}
              {user && testCreatorId && user.id === testCreatorId ? (
                <>
                  {/* Hiring Recommendation */}
                  {selectedCandidate.recommendationForHiring && (
                    <div className={`rounded-xl border p-6 ${
                      selectedCandidate.recommendationForHiring === "STRONG RECOMMEND" ? "bg-green-50 border-green-200" :
                      selectedCandidate.recommendationForHiring === "RECOMMEND" ? "bg-blue-50 border-blue-200" :
                      selectedCandidate.recommendationForHiring === "NEUTRAL" ? "bg-yellow-50 border-yellow-200" :
                      selectedCandidate.recommendationForHiring === "NOT RECOMMEND" ? "bg-orange-50 border-orange-200" :
                      "bg-red-50 border-red-200"
                    }`}>
                      <div className="flex items-start space-x-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          selectedCandidate.recommendationForHiring === "STRONG RECOMMEND" ? "bg-green-100" :
                          selectedCandidate.recommendationForHiring === "RECOMMEND" ? "bg-blue-100" :
                          selectedCandidate.recommendationForHiring === "NEUTRAL" ? "bg-yellow-100" :
                          selectedCandidate.recommendationForHiring === "NOT RECOMMEND" ? "bg-orange-100" :
                          "bg-red-100"
                        }`}>
                          <Trophy className={`w-6 h-6 ${
                            selectedCandidate.recommendationForHiring === "STRONG RECOMMEND" ? "text-green-600" :
                            selectedCandidate.recommendationForHiring === "RECOMMEND" ? "text-blue-600" :
                            selectedCandidate.recommendationForHiring === "NEUTRAL" ? "text-yellow-600" :
                            selectedCandidate.recommendationForHiring === "NOT RECOMMEND" ? "text-orange-600" :
                            "text-red-600"
                          }`} />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">
                            Hiring Recommendation
                          </h4>
                          <p className={`font-bold text-2xl mb-2 ${
                            selectedCandidate.recommendationForHiring === "STRONG RECOMMEND" ? "text-green-700" :
                            selectedCandidate.recommendationForHiring === "RECOMMEND" ? "text-blue-700" :
                            selectedCandidate.recommendationForHiring === "NEUTRAL" ? "text-yellow-700" :
                            selectedCandidate.recommendationForHiring === "NOT RECOMMEND" ? "text-orange-700" :
                            "text-red-700"
                          }`}>
                            {selectedCandidate.recommendationForHiring}
                          </p>
                          {selectedCandidate.keyFindings && (
                            <p className="text-gray-700 leading-relaxed">
                              {selectedCandidate.keyFindings}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Show message if interview not completed */}
                  {!selectedCandidate.hasCompletedInterview && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-blue-800 text-sm flex items-center">
                        <AlertCircle className="w-4 h-4 mr-2" />
                        This candidate has only completed Round 1 (MCQ). Detailed AI assessment will be available after they complete Round 2 (AI Interview).
                      </p>
                    </div>
                  )}

                  {/* Detailed Assessment */}
                  {selectedAssessment && (
                    <>
                      {/* Performance Breakdown - only show if detailedScoring exists */}
                      {selectedAssessment.detailedScoring && (
                        <div className="bg-gray-50 rounded-lg p-6">
                          <h4 className="text-lg font-semibold text-gray-900 mb-4">Performance Breakdown</h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-700">Technical Knowledge</span>
                              <span className="font-semibold text-lg">{selectedAssessment.detailedScoring.technicalKnowledge ?? 0}/25</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-700">Problem Solving</span>
                              <span className="font-semibold text-lg">{selectedAssessment.detailedScoring.problemSolving ?? 0}/25</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-700">Communication</span>
                              <span className="font-semibold text-lg">{selectedAssessment.detailedScoring.communication ?? 0}/15</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-700">Job Fit</span>
                              <span className="font-semibold text-lg">{selectedAssessment.detailedScoring.jobFit ?? 0}/10</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Overall Assessment - only show if narrativeAssessment exists */}
                      {selectedAssessment.narrativeAssessment && (
                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                          <h4 className="text-lg font-semibold text-gray-900 mb-4">Overall Assessment</h4>
                          <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                            {selectedAssessment.narrativeAssessment}
                          </p>
                        </div>
                      )}

                      {/* Strengths & Weaknesses - only show if at least one exists */}
                      {(selectedAssessment.strengths?.length || selectedAssessment.weaknesses?.length) && (
                        <div className="grid md:grid-cols-2 gap-6">
                          {selectedAssessment.strengths?.length > 0 && (
                            <div className="bg-green-50 rounded-lg border border-green-200 p-6">
                              <h4 className="text-lg font-semibold text-green-900 mb-4 flex items-center">
                                <CheckCircle className="w-5 h-5 mr-2" />
                                Strengths
                              </h4>
                              <ul className="space-y-2">
                                {selectedAssessment.strengths.map((strength, index) => (
                                  <li key={index} className="text-sm text-gray-700 flex items-start">
                                    <Star className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                                    {strength}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {selectedAssessment.weaknesses?.length > 0 && (
                            <div className="bg-orange-50 rounded-lg border border-orange-200 p-6">
                              <h4 className="text-lg font-semibold text-orange-900 mb-4 flex items-center">
                                <Star className="w-5 h-5 mr-2" />
                                Areas for Improvement
                              </h4>
                              <ul className="space-y-2">
                                {selectedAssessment.weaknesses.map((weakness, index) => (
                                  <li key={index} className="text-sm text-gray-700 flex items-start">
                                    <span className="w-3 h-3 border border-orange-400 rounded-full mr-2 mt-1 flex-shrink-0"></span>
                                    {weakness}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                  <p className="text-yellow-800 font-medium">
                    🔒 Only the test creator can view detailed assessment
                  </p>
                  <p className="text-sm text-yellow-700 mt-2">
                    You can only see the candidate's scores. Full assessment details are restricted to the HR who created this test.
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              {user && testCreatorId && user.id === testCreatorId && (() => {
                const eligibility = canShortlistOrReject(selectedCandidate);
                return (
                  <div className="pt-4 border-t border-gray-200">
                    {!eligibility.canAction && (
                      <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                          {eligibility.reason}
                        </p>
                      </div>
                    )}
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleShortlist(selectedCandidate)}
                        disabled={!eligibility.canAction}
                        className={`flex-1 flex items-center justify-center px-4 py-3 rounded-lg font-medium transition-colors ${
                          eligibility.canAction
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Shortlist Candidate
                      </button>
                      <button
                        onClick={() => handleReject(selectedCandidate)}
                        disabled={!eligibility.canAction}
                        className={`flex-1 flex items-center justify-center px-4 py-3 rounded-lg font-medium transition-colors ${
                          eligibility.canAction
                            ? 'bg-red-600 text-white hover:bg-red-700'
                            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        <XCircle className="w-5 h-5 mr-2" />
                        Reject Candidate
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestResults;
